import type { AppliedFilter, DateFilterOption, NumberRangeFilterOption } from 'store/entities/drawer';
import { isDateFilterOption, isNumberRangeFilterOption, isTableValueArray } from 'store/entities/drawer';
import type { FilterDate } from 'types/filters';
import { ComparisonOperator, TemplatePresentationItemDisplay } from 'types/dataHelios';
import type { FilterCondition } from 'types/tableFilters';

export const extractDateFilterValue = (item: AppliedFilter, actualFormItemId: string, referencedFormItemId?: string) => {
  if (isDateFilterOption(item.value)) {
    const dateValue = item.value as DateFilterOption;
    const { fromDate, toDate } = dateValue;

    return {
      formItemId: actualFormItemId,
      formItemType: item.formItemType,
      fromValue: fromDate,
      toValue: toDate,
      referencedFormItemId,
    };
  }

  const fallbackValue = (item.value as FilterDate)?.fullDate || String(item.value || '');
  return {
    formItemId: actualFormItemId,
    formItemType: item.formItemType,
    fromValue: fallbackValue,
    toValue: fallbackValue,
    referencedFormItemId,
  };
};

export const extractNumberFilterValue = (item: AppliedFilter, actualFormItemId: string, referencedFormItemId?: string) => {
  if (isNumberRangeFilterOption(item.value)) {
    const numberValue = item.value as NumberRangeFilterOption;
    const { fromNumber, toNumber } = numberValue;

    return {
      formItemId: actualFormItemId,
      formItemType: item.formItemType,
      fromValue: fromNumber?.toString() || undefined,
      toValue: toNumber?.toString() || undefined,
      referencedFormItemId,
    };
  }

  const fallbackValue = String(item.value || '');
  return {
    formItemId: actualFormItemId,
    formItemType: item.formItemType,
    fromValue: fallbackValue,
    toValue: fallbackValue,
    referencedFormItemId,
  };
};

export const extractOtherFilterValues = (item: AppliedFilter): string[] => {
  if (item.formItemType === 'Tables') {
    return isTableValueArray(item.value) ? item.value.map((v) => v.value) : [String(item.value || '')];
  }

  if (item.formItemType === 'Users') {
    return Array.isArray(item.value) ? item.value.map(String) : [String(item.value || '')];
  }

  return [String(item.value || '')];
};

export const extractFilterValues = (filters: AppliedFilter[] = [], referencedFormItemsMap?: Record<string, string>) => {
  const referencedMap = new Map(Object.entries(referencedFormItemsMap || {}));

  return filters.map((item) => {
    const formItemId = item.formItemId;
    const parentFormItemId = referencedMap.get(formItemId);
    const actualFormItemId = parentFormItemId || formItemId;
    const referencedFormItemId = parentFormItemId ? formItemId : undefined;

    if (item.formItemType === 'Date') {
      return extractDateFilterValue(item, actualFormItemId, referencedFormItemId);
    }

    if (item.formItemType === 'Number') {
      return extractNumberFilterValue(item, actualFormItemId, referencedFormItemId);
    }

    const values = extractOtherFilterValues(item);

    return {
      formItemId: actualFormItemId,
      formItemType: item.formItemType,
      values,
      referencedFormItemId,
    };
  });
};

export const isValueEmpty = (value: unknown) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return true;
    }

    if (isTableValueArray(value)) {
      return value.every((v) => [undefined, null, ''].includes(v.value));
    }

    return value.every((v) => [undefined, null, ''].includes(v));
  }

  if (isDateFilterOption(value) || isNumberRangeFilterOption(value)) {
    return Object.values(value).every((v) => [undefined, null, ''].includes(v));
  }

  return !value;
};

export const getImplicitComparisonOperator = (filter: FilterCondition): ComparisonOperator => {
  if (filter.isBlank === true) return ComparisonOperator.Empty;
  if (filter.isBlank === false) return ComparisonOperator.NotEmpty;

  if (filter.displayType === TemplatePresentationItemDisplay.StaticSelection) {
    return ComparisonOperator.Equals;
  }

  if (filter.displayType === TemplatePresentationItemDisplay.Browse) {
    return ComparisonOperator.Equals;
  }

  if (filter.dataType === 'String' || filter.dataType === 'GuidArray') {
    return ComparisonOperator.Contains;
  }

  if (filter.dataType === 'Boolean') {
    return ComparisonOperator.Equals;
  }

  const from = filter.fromValue;
  const to = filter.toValue;

  if (from && !to) return ComparisonOperator.GreaterThanOrEqual;
  if (!from && to) return ComparisonOperator.LessThanOrEqual;
  if (from && to) {
    const fromNum = Number(from);
    const toNum = Number(to);
    if (!isNaN(fromNum) && !isNaN(toNum)) {
      if (fromNum === toNum) return ComparisonOperator.Equals;
      return ComparisonOperator.Between;
    }
    if (from === to) return ComparisonOperator.Equals;
    return ComparisonOperator.Between;
  }

  return ComparisonOperator.Equals;
};
